<template>
  <div class="platform-subscription-page">
    <div class="page-header">
      <h1>Platform Subscription</h1>
      <p class="subtitle">Unlimited access to all content</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="6" animated />
    </div>

    <!-- Active Subscription -->
    <div v-else-if="hasActiveSubscription" class="subscription-active">
      <el-card class="status-card">
        <div class="status-header">
          <div class="status-badge active">
            <i class="el-icon-check"></i>
            {{ subscription.status === 'CANCELLED' ? 'Subscription Ending' : 'Active Subscription' }}
          </div>
        </div>

        <div class="subscription-details">
          <div class="detail-row">
            <span class="label">Plan</span>
            <span class="value">{{ getPlanName(subscription.plan_key) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Access Until</span>
            <span class="value">{{ formatDate(subscription.end_date) }}</span>
          </div>
          <div v-if="subscription.auto_renew" class="detail-row">
            <span class="label">Auto Renew</span>
            <span class="value">
              <el-tag size="small">Enabled</el-tag>
            </span>
          </div>
        </div>

        <div class="subscription-actions">
          <el-button
            v-if="subscription.status === 'ACTIVE'"
            type="danger"
            plain
            @click="handleCancel"
            :loading="actionLoading"
          >
            Cancel Subscription
          </el-button>
          <el-button
            v-else-if="subscription.status === 'CANCELLED'"
            type="primary"
            @click="handleResume"
            :loading="actionLoading"
          >
            Resume Subscription
          </el-button>
          <el-button @click="handleManageBilling">
            Manage Billing
          </el-button>
        </div>
      </el-card>

      <!-- Benefits Section -->
      <el-card class="benefits-card">
        <h3>Your Benefits</h3>
        <ul class="benefits-list">
          <li><i class="el-icon-check"></i> Unlimited access to all collections and bundles</li>
          <li><i class="el-icon-check"></i> Unlock all exclusive and subscribers-only content</li>
          <li><i class="el-icon-check"></i> Access all cryptic (hidden) cards within collections</li>
          <li><i class="el-icon-check"></i> New content added regularly</li>
          <li><i class="el-icon-check"></i> Cancel anytime</li>
        </ul>
      </el-card>

      <!-- Billing History -->
      <el-card class="billing-card">
        <h3>Billing History</h3>
        <div v-if="billingLoading" style="text-align: center; padding: 20px;">
          <i class="el-icon-loading"></i> Loading...
        </div>
        <div v-else-if="billingHistory.length === 0" style="text-align: center; padding: 20px; color: #909399;">
          No billing records found
        </div>
        <el-table v-else :data="billingHistory" style="width: 100%" size="small">
          <el-table-column label="Date" width="180">
            <template slot-scope="scope">
              {{ formatDate(scope.row.create_time) }}
            </template>
          </el-table-column>
          <el-table-column label="Plan" min-width="160">
            <template slot-scope="scope">
              <span style="white-space: nowrap;">{{ getPlanName(scope.row.plan_key) }}</span>
              <el-tag v-if="scope.row.is_trial" type="info" size="mini" style="margin-left: 4px;">Trial</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Amount" width="100">
            <template slot-scope="scope">
              ${{ scope.row.usd_price }}
            </template>
          </el-table-column>
          <el-table-column label="Status" width="120">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'ACTIVE' ? '' : (scope.row.status === 'EXPIRED' ? 'info' : 'warning')" size="small">
                {{ scope.row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Valid Until" width="160">
            <template slot-scope="scope">
              {{ formatDate(scope.row.end_date) }}
            </template>
          </el-table-column>
          <el-table-column label="Receipt" width="100" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.stripe_session_id"
                type="text"
                size="small"
                @click="viewReceipt(scope.row)"
              >
                <i class="el-icon-document"></i> View
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- No Subscription - Show Plans -->
    <div v-else class="subscription-plans">
      <div class="plans-grid">
        <!-- Trial Plan -->
        <el-card
          class="plan-card trial"
          :class="{ selected: selectedPlan === 'trial' }"
          @click.native="selectPlan('trial')"
          v-if="trialEligible"
        >
          <div class="plan-badge">Try It</div>
          <h3 class="plan-name">7-Day Trial</h3>
          <div class="plan-price">
            <span class="amount">$1</span>
            <span class="period">for 7 days</span>
          </div>
          <p class="plan-description">Try the full platform for just $1</p>
          <ul class="plan-features">
            <li><i class="el-icon-check"></i> Full access for 7 days</li>
            <li><i class="el-icon-check"></i> Then $10/month</li>
            <li><i class="el-icon-check"></i> Cancel anytime</li>
          </ul>
        </el-card>

        <!-- Monthly Plan -->
        <el-card
          class="plan-card monthly"
          :class="{ selected: selectedPlan === 'monthly' }"
          @click.native="selectPlan('monthly')"
        >
          <div class="plan-badge popular">Most Popular</div>
          <h3 class="plan-name">Monthly</h3>
          <div class="plan-price">
            <span class="amount">$10</span>
            <span class="period">/month</span>
          </div>
          <p class="plan-description">Flexible monthly billing</p>
          <ul class="plan-features">
            <li><i class="el-icon-check"></i> Unlimited access</li>
            <li><i class="el-icon-check"></i> All content included</li>
            <li><i class="el-icon-check"></i> Cancel anytime</li>
          </ul>
        </el-card>

        <!-- Yearly Plan -->
        <el-card
          class="plan-card yearly"
          :class="{ selected: selectedPlan === 'yearly' }"
          @click.native="selectPlan('yearly')"
        >
          <div class="plan-badge savings">Save 20%</div>
          <h3 class="plan-name">Yearly</h3>
          <div class="plan-price">
            <span class="amount">$96</span>
            <span class="period">/year</span>
          </div>
          <p class="plan-description">$8/month, billed annually</p>
          <ul class="plan-features">
            <li><i class="el-icon-check"></i> Unlimited access</li>
            <li><i class="el-icon-check"></i> Save $24/year</li>
            <li><i class="el-icon-check"></i> Cancel anytime</li>
          </ul>
        </el-card>
      </div>

      <!-- Terms Agreement -->
      <div class="terms-section">
        <el-checkbox v-model="agreedToTerms">
          I agree to the <a href="/#/terms-of-service" target="_blank">Terms of Service</a> and
          <a href="/#/privacy-policy" target="_blank">Privacy Policy</a>
        </el-checkbox>
      </div>

      <!-- Subscribe Button -->
      <div class="subscribe-section">
        <el-button
          type="primary"
          size="large"
          :disabled="!selectedPlan || !agreedToTerms"
          :loading="subscribing"
          @click="handleSubscribe"
        >
          {{ getSubscribeButtonText() }}
        </el-button>
        <p class="secure-note">
          <i class="el-icon-lock"></i> Secure payment powered by Stripe
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'PlatformSubscription',
  data() {
    return {
      loading: true,
      subscribing: false,
      actionLoading: false,
      subscription: null,
      trialEligible: false,
      selectedPlan: 'monthly',
      agreedToTerms: false,
      billingHistory: [],
      billingLoading: false
    }
  },
  computed: {
    hasActiveSubscription() {
      return this.subscription &&
        ['ACTIVE', 'CANCELLED', 'COMPLETE'].includes(this.subscription.status) &&
        this.subscription.end_date > Math.floor(Date.now() / 1000)
    }
  },
  created() {
    this.fetchSubscriptionStatus()
    this.fetchBillingHistory()
  },
  methods: {
    async fetchSubscriptionStatus() {
      this.loading = true
      try {
        const response = await postRequest('getPlatformSubscription', {
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          const data = response.data.data
          this.subscription = data.subscription
          this.trialEligible = data.trial_eligible

          // Update store
          this.$store.commit('setPlatformSubscription', data)

          // Default to trial if eligible, otherwise monthly
          if (this.trialEligible) {
            this.selectedPlan = 'trial'
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error)
        this.$message.error('Failed to load subscription status')
      } finally {
        this.loading = false
      }
    },

    async fetchBillingHistory() {
      this.billingLoading = true
      try {
        const response = await postRequest('getPlatformSubscriptionHistory', {
          user_id: this.$store.state.user_id
        })
        if (response.data.code === 0) {
          this.billingHistory = response.data.data || []
        }
      } catch (error) {
        console.error('Error fetching billing history:', error)
      } finally {
        this.billingLoading = false
      }
    },

    async viewReceipt(record) {
      try {
        const response = await postRequest('createPlatformSubscriptionPortal', {
          user_id: this.$store.state.user_id,
          return_url: window.location.href
        })
        if (response.data.code === 0 && response.data.data.url) {
          window.open(response.data.data.url, '_blank')
        } else {
          this.$message.error('Failed to open billing portal')
        }
      } catch (error) {
        this.$message.error('Failed to open billing portal')
      }
    },

    selectPlan(plan) {
      this.selectedPlan = plan
    },

    getPlanName(planKey) {
      const names = {
        'trial': '7-Day Trial',
        'monthly': 'Monthly',
        'yearly': 'Yearly'
      }
      return names[planKey] || planKey
    },

    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getSubscribeButtonText() {
      if (!this.selectedPlan) return 'Select a Plan'
      if (this.selectedPlan === 'trial') return 'Start 7-Day Trial for $1'
      if (this.selectedPlan === 'monthly') return 'Subscribe for $10/month'
      if (this.selectedPlan === 'yearly') return 'Subscribe for $96/year'
      return 'Subscribe'
    },

    async handleSubscribe() {
      if (!this.selectedPlan || !this.agreedToTerms) return

      this.subscribing = true
      try {
        const response = await postRequest('createPlatformSubscription', {
          user_id: this.$store.state.user_id,
          plan_key: this.selectedPlan,
          trial: this.selectedPlan === 'trial',
          success_url: window.location.origin + '/#/platform-subscription?success=true',
          cancel_url: window.location.origin + '/#/platform-subscription?cancelled=true'
        })

        if (response.data.code === 0 && response.data.data.checkout_url) {
          // Redirect to Stripe Checkout
          window.location.href = response.data.data.checkout_url
        } else {
          this.$message.error(response.data.msg || 'Failed to create checkout session')
        }
      } catch (error) {
        console.error('Error creating subscription:', error)
        this.$message.error('Failed to start subscription process')
      } finally {
        this.subscribing = false
      }
    },

    async handleCancel() {
      try {
        await this.$confirm(
          'Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.',
          'Cancel Subscription',
          {
            confirmButtonText: 'Yes, Cancel',
            cancelButtonText: 'Keep Subscription',
            type: 'warning'
          }
        )

        this.actionLoading = true
        const response = await postRequest('cancelPlatformSubscription', {
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.$message.success('Subscription cancelled. You have access until ' + this.formatDate(this.subscription.end_date))
          this.fetchSubscriptionStatus()
        } else {
          this.$message.error(response.data.msg || 'Failed to cancel subscription')
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error('Error cancelling subscription:', e)
          this.$message.error('Failed to cancel subscription')
        }
      } finally {
        this.actionLoading = false
      }
    },

    async handleResume() {
      this.actionLoading = true
      try {
        const response = await postRequest('resumePlatformSubscription', {
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.$message.success('Subscription resumed!')
          this.fetchSubscriptionStatus()
        } else {
          this.$message.error(response.data.msg || 'Failed to resume subscription')
        }
      } catch (error) {
        console.error('Error resuming subscription:', error)
        this.$message.error('Failed to resume subscription')
      } finally {
        this.actionLoading = false
      }
    },

    async handleManageBilling() {
      this.actionLoading = true
      try {
        const response = await postRequest('createPlatformSubscriptionPortal', {
          user_id: this.$store.state.user_id,
          return_url: window.location.href
        })

        if (response.data.code === 0 && response.data.data.url) {
          window.location.href = response.data.data.url
        } else {
          this.$message.error(response.data.msg || 'Failed to open billing portal')
        }
      } catch (error) {
        console.error('Error opening billing portal:', error)
        this.$message.error('Failed to open billing portal')
      } finally {
        this.actionLoading = false
      }
    }
  }
}
</script>

<style scoped>
.platform-subscription-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 32px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.page-header .subtitle {
  font-size: 16px;
  color: #909399;
}

.loading-container {
  padding: 40px;
}

/* Active Subscription Styles */
.subscription-active {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.status-card {
  border-radius: 12px;
}

.status-header {
  margin-bottom: 24px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 500;
}

.status-badge.active {
  background: #ecf5ff;
  color: #409eff;
}

.subscription-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.detail-row .label {
  color: #909399;
  font-size: 14px;
}

.detail-row .value {
  color: #303133;
  font-weight: 500;
}

.subscription-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.benefits-card h3 {
  margin-bottom: 16px;
  color: #303133;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  color: #606266;
}

.benefits-list li i {
  color: #67c23a;
}

.billing-card {
  border-radius: 12px;
}

.billing-card h3 {
  margin-bottom: 16px;
  color: #303133;
}

/* Plans Grid Styles */
.plans-grid {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  justify-content: center;
  align-items: stretch;
}

.plan-card {
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  padding-top: 40px;
  flex: 1;
  min-width: 0;
  max-width: 300px;
  overflow: visible;
}

.plan-card >>> .el-card__body {
  padding: 20px 24px 28px;
  overflow: visible;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.plan-card.selected {
  border: 2px solid #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.plan-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #909399;
  color: white;
  text-align: center;
  white-space: nowrap;
}

.plan-badge.popular {
  background: #409eff;
}

.plan-badge.savings {
  background: #67c23a;
}

.plan-name {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  text-align: center;
}

.plan-price {
  text-align: center;
  margin-bottom: 12px;
}

.plan-price .amount {
  font-size: 36px;
  font-weight: 700;
  color: #303133;
}

.plan-price .period {
  font-size: 14px;
  color: #909399;
}

.plan-description {
  text-align: center;
  color: #909399;
  font-size: 14px;
  margin-bottom: 20px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: #606266;
  font-size: 14px;
}

.plan-features li i {
  color: #67c23a;
}

/* Terms & Subscribe Section */
.terms-section {
  text-align: center;
  margin-bottom: 24px;
}

.terms-section a {
  color: #409eff;
  text-decoration: none;
}

.terms-section a:hover {
  text-decoration: underline;
}

.subscribe-section {
  text-align: center;
}

.subscribe-section .el-button {
  min-width: 280px;
  height: 48px;
  font-size: 16px;
}

.secure-note {
  margin-top: 12px;
  color: #909399;
  font-size: 13px;
}

.secure-note i {
  margin-right: 4px;
}

/* Responsive */
@media (max-width: 768px) {
  .platform-subscription-page {
    padding: 20px 16px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .plans-grid {
    flex-direction: column;
    align-items: center;
  }

  .plan-card {
    max-width: 100%;
    width: 100%;
  }

  .subscription-actions {
    flex-direction: column;
  }

  .subscription-actions .el-button {
    width: 100%;
  }
}
</style>
